import Phaser from 'phaser';
import { GAME } from '../constants';
import { RunData } from '../run/RunState';
import { loadMetaProfile, saveMetaProfile, updateMetaFromRunResult } from '../meta/Profile';
import { PIXEL_THEME, FONT_FAMILY, createPixelPanel, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut } from '../ui/SceneTransitions';
import { getDefeatLine } from '../story/Lore';

export class GameOverScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameOverScene' });
  }

  create(data: {
    victory: boolean;
    turnsPlayed: number;
    playerHp: number;
    maxHp: number;
    isRunComplete?: boolean;
    combos?: number;
    elites?: number;
    bosses?: number;
    relics?: number;
    run?: RunData;
  }): void {
    const profile = loadMetaProfile();
    const updatedProfile = updateMetaFromRunResult(profile, {
      victory: Boolean(data.isRunComplete || data.victory),
      elitesDefeated: data.run?.elitesDefeated ?? data.elites ?? 0,
      bossesDefeated: data.run?.bossesDefeated ?? data.bosses ?? 0,
      combosTriggered: data.run?.combosTriggered ?? data.combos ?? 0,
    });
    saveMetaProfile(updatedProfile);

    fadeIn(this);
    const cx = GAME.WIDTH / 2;
    const cy = GAME.HEIGHT / 2;
    drawPixelBackdrop(this);

    let title: string;
    let color: string;
    if (data.isRunComplete) {
      title = 'RUN COMPLETE!';
      color = '#ffd700';
    } else if (data.victory) {
      title = 'VICTORY';
      color = '#ffd700';
    } else {
      title = 'DEFEAT';
      color = '#c62828';
    }

    const heroPanel = createPixelPanel(this, cx, cy - 104, GAME.WIDTH - 170, 196, {
      fill: data.victory ? 0x1f2338 : 0x2d1d2a,
      stroke: data.victory ? 0x708bc7 : 0xbe5a62,
      accent: data.victory ? 0xffd58f : 0xffa3b2,
    });
    heroPanel.elements.forEach(el => el.setDepth(1));

    const profilePanel = createPixelPanel(this, cx, cy + 94, GAME.WIDTH - 170, 96, {
      fill: 0x161c2f,
      stroke: 0x56729f,
      accent: 0x99b6e8,
    });
    profilePanel.elements.forEach(el => el.setDepth(1));

    this.add.text(cx, cy - 140, title, {
      fontSize: '28px',
      fontFamily: FONT_FAMILY,
      color,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    const flavor = data.isRunComplete
      ? 'The Ledger is balanced. The count goes quiet for a while.'
      : getDefeatLine(data.run?.bossesDefeated ?? data.bosses ?? 0);
    this.add.text(cx, cy - 108, flavor, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: data.isRunComplete ? '#ffe1a6' : '#d6a3ad',
      fontStyle: 'italic',
      align: 'center',
      wordWrap: { width: GAME.WIDTH - 230 },
      lineSpacing: 4,
    }).setOrigin(0.5).setDepth(2);

    const stats = [
      `Battles Won: ${data.turnsPlayed}`,
      `HP: ${data.playerHp}/${data.maxHp}`,
    ];
    if (data.combos !== undefined) stats.push(`Combos Triggered: ${data.combos}`);
    if (data.elites !== undefined) stats.push(`Elites Defeated: ${data.elites}`);
    if (data.bosses !== undefined) stats.push(`Bosses Defeated: ${data.bosses}`);
    if (data.relics !== undefined) stats.push(`Relics Collected: ${data.relics}`);

    this.add.text(cx, cy - 24, stats.join('\n'), {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#cfdaf3',
      align: 'center',
      lineSpacing: 7,
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, cy + 76,
      `Profile: runs ${updatedProfile.stats.runs} | wins ${updatedProfile.stats.wins} | elites ${updatedProfile.stats.elites} | bosses ${updatedProfile.stats.bosses} | combos ${updatedProfile.stats.combos}`,
      {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: PIXEL_THEME.textMuted,
      }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, cy + 98,
      `Unlocks now: rarity ${updatedProfile.unlocks.maxRarity.toUpperCase()} | event pack ${updatedProfile.unlocks.eventPack} | curse pack ${updatedProfile.unlocks.cursePack} | ability pack ${updatedProfile.unlocks.abilityPack}`,
      {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#ffd166',
        align: 'center',
      }).setOrigin(0.5).setDepth(2);

    const btn = this.add.text(cx, cy + 162, '[ MAIN MENU ]', {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#ffffff',
      backgroundColor: '#2f3555',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(3);
    btn.setStroke('#0d1326', 4);

    btn.on('pointerover', () => btn.setColor('#ffd700'));
    btn.on('pointerout', () => btn.setColor('#ffffff'));
    btn.on('pointerdown', () => {
      fadeOut(this).then(() => {
        this.scene.stop();
        this.scene.start('MainMenuScene');
      });
    });
  }
}
